import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { GET_PARENT } from '@/modules/API/parent/parentHandler'
import Loading from '@/components/Loading/Loading'
import styles from './parentChilds.module.scss'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import TableChild from './Table/childTable'
import NoChildsDialog from './Dialog/noChilds'
import InfoChildDialog from './Dialog/infoChild'
import { Check } from '@mui/icons-material'
import { Child } from '@/types/Child'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface parentChildsProps {
    signUp?: boolean
    signUpAutolink?: boolean
}

const ParentChilds: FunctionComponent<parentChildsProps> = ({ signUp, signUpAutolink }: parentChildsProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess } = useNotification()

    const [childs, setChilds] = useState<Array<Child>>([])
    const [isNanny, setIsNanny] = useState<boolean>(false)

    const [isBelgian, setIsBelgian] = useState<boolean>(false)

    const [isDialogNoChildsOpen, setDialogNoChildsOpen] = useState<boolean>(false)
    const [isDialogChildOpen, setDialogChildOpen] = useState<boolean>(!!signUp)

    const {
        loading: loadingParent,
        error: errorParent,
        data: dataParent,
        refetch: refetchParent
    } = useQuery(GET_PARENT)

    useEffect(() => {
        if (dataParent) {
            setChilds(dataParent.getParent.childs)
            setIsNanny(dataParent.getParent.isNanny)
            setIsBelgian(dataParent.getParent.country === 'BE')
        }
    }, [dataParent])

    async function refetchOneParent(): Promise<void> {
        const { data } = await refetchParent()
        if (data) {
            setChilds(data.getParent.childs)
        }
    }

    function endSignUp(): void {
        if (childs && childs.length > 0) {
            showNotificationSuccess(t('endSignUp'))
            nextStep()
        } else {
            setDialogNoChildsOpen(true)
        }
    }

    function nextStep(): void {
        navigate('/daycare')
    }

    function backToHome(): void {
        navigate('/home')
    }

    useEffect(() => {
        setDialogChildOpen(!!signUp || !!signUpAutolink)
    }, [signUp, signUpAutolink])

    return (
        <>
            {loadingParent && <Loading overall />}
            <div className={styles.PagePosition}>
                <div className={styles.ProfilPlacement}>
                    {dataParent && (
                        <div className={styles.Form}>
                            <div className={styles.Title}>
                                <Title text={t('myChilds')} />
                                <ButtonTextIcon type={'add'} onClick={() => setDialogChildOpen(true)} parent />
                            </div>
                            <div className={styles.InputContainer}>
                                <TableChild
                                    getParent={refetchOneParent}
                                    childs={childs}
                                    isNanny={isNanny}
                                    isBelgian={isBelgian}
                                />
                            </div>
                            {signUp || signUpAutolink ? (
                                <ButtonTextIcon
                                    type={'custom'}
                                    customText={t('end')}
                                    customIcon={<Check />}
                                    onClick={endSignUp}
                                    parent
                                    className={styles.Button}
                                />
                            ) : (
                                <ButtonTextIcon type={'return'} onClick={backToHome} parent className={styles.Button} />
                            )}
                        </div>
                    )}
                    {errorParent && <ErrorMessage error={t('generalError')} />}
                </div>
            </div>
            <NoChildsDialog
                open={isDialogNoChildsOpen}
                handleClose={() => setDialogNoChildsOpen(false)}
                addChild={() => setDialogChildOpen(true)}
                nextStep={nextStep}
            />
            <InfoChildDialog
                open={isDialogChildOpen}
                handleClose={() => setDialogChildOpen(false)}
                getParent={refetchOneParent}
                child={null}
                isBelgian={isBelgian}
                signUpAutolink={!!signUpAutolink}
            />
        </>
    )
}

export default ParentChilds
